import React, {Suspense} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay/hooks'
import {Box, BoxProps, Divider, Grid} from '@chakra-ui/react'
import {
  faEnvelope as falEnvelope,
  faMapMarkerAlt as falMapMarkerAlt,
  faPhone as falPhone,
} from '@fortawesome/pro-light-svg-icons'

import {ProviderInfoCardQuery} from './__generated__/ProviderInfoCardQuery.graphql'
import {ProviderInfoCardGallery} from './ProviderInfoCardGallery'
import {ProviderInfoCardSkeleton} from './ProviderInfoCardSkeleton'
import {FontAwesomeIcon} from 'components/FontAwesomeIcon'
import {useSiteEnvironmentContext} from 'hooks'

export interface ProviderInfoCardProps {
  slug: string
}

export function ProviderInfoCardSuspense(
  props: ProviderInfoCardProps & BoxProps
) {
  return (
    <Suspense fallback={<ProviderInfoCardSkeleton />}>
      <ProviderInfoCard {...props} />
    </Suspense>
  )
}

export function ProviderInfoCard({
  slug,
  ...props
}: ProviderInfoCardProps & BoxProps) {
  const {IS_YOUTH_DOMAIN} = useSiteEnvironmentContext()

  const {provider} = useLazyLoadQuery<ProviderInfoCardQuery>(
    graphql`
      query ProviderInfoCardQuery($slug: String!) {
        provider(slug: $slug) {
          address
          contactPhone
          description(format: HTML)
          descriptionYouth(format: HTML)
          email
          publicEmail
          postalCode
          city {
            name
            province {
              shortName
            }
          }
          images(collection: "sliders", first: 10) {
            pageInfo {
              count
            }
            edges {
              node {
                ...ProviderInfoCardGallery_images
              }
            }
          }
        }
      }
    `,
    {slug}
  )

  const description = IS_YOUTH_DOMAIN
    ? provider.descriptionYouth
    : provider.description

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      overflow="hidden"
      {...props}
    >
      {provider.images.pageInfo.count > 0 && (
        <ProviderInfoCardGallery
          images={provider.images.edges.map(({node}) => node)}
        />
      )}

      <Box p={4}>
        <Box
          dangerouslySetInnerHTML={{__html: description}}
          fontSize="sm"
          sx={{
            '& > p': {
              mb: 2,
            },
            '& > p:last-child': {
              mb: 0,
            },
          }}
        />
      </Box>

      <Divider borderColor="gray.100" m={0} />

      <Grid
        fontSize="sm"
        gap={4}
        p={4}
        templateColumns={[`1fr`, `1fr`, `2fr 1fr`]}
        sx={{
          '& svg': {
            color: `green.500`,
          },
        }}
      >
        {provider.publicEmail && (
          <a href={`mailto:${provider.publicEmail}`}>
            <FontAwesomeIcon icon={falEnvelope} />
            {` `}
            {provider.publicEmail}
          </a>
        )}
        {provider.contactPhone && (
          <a href={`tel:${provider.contactPhone}`}>
            <FontAwesomeIcon icon={falPhone} />
            {` `}
            {provider.contactPhone}
          </a>
        )}

        {provider.address && provider.city && (
          <div>
            <FontAwesomeIcon icon={falMapMarkerAlt} />
            {` `}
            <span>
              {`${provider.address}, ${provider.city.name}, ${
                provider.city.province.shortName
              } ${provider.postalCode ?? ``}`.trim()}
            </span>
          </div>
        )}
      </Grid>
    </Box>
  )
}
